'use client';

import React, { ReactNode } from 'react';

import {
    DirectionEnum,
    FlexBox,
    GapSize,
    Title,
    TitleLevels,
    TitleMarkup,
    TypographyAlign,
} from '@bytel/trilogy-react';

import { SectionContainer } from '@components/section-container';

import { FaqSlugType, partials } from '@helpers/partials';

import { SSIBlock } from './ssi-block';

const getTitleLevelFromMarkup = (markup: TitleMarkup): TitleLevels => {
    switch (markup) {
        case TitleMarkup.H1:
            return TitleLevels.ONE;
        case TitleMarkup.H2:
            return TitleLevels.TWO;
        case TitleMarkup.H3:
            return TitleLevels.THREE;
        case TitleMarkup.H4:
            return TitleLevels.FOUR;
        case TitleMarkup.H5:
            return TitleLevels.FIVE;
        case TitleMarkup.H6:
            return TitleLevels.SIX;
        default:
            return TitleLevels.TWO;
    }
};

type FaqProps = {
    url: FaqSlugType;
    title: string | ReactNode;
    titleAlt?: string;
    titleMarkup?: TitleMarkup;
    titleLevel?: TitleLevels;
    withSection?: boolean;
    className?: string;
};

type FaqContainerProps = {
    title: string | ReactNode;
    titleAlt?: string;
    children: ReactNode;
    titleMarkup?: TitleMarkup;
    titleLevel?: TitleLevels;
    className?: string;
};

function FaqContainer({
    title,
    titleAlt,
    children,
    titleMarkup = TitleMarkup.H2,
    titleLevel = getTitleLevelFromMarkup(titleMarkup),
    className,
}: Readonly<FaqContainerProps>) {
    return (
        <FlexBox direction={DirectionEnum.COLUMN} gap={GapSize.FIVE} className={className}>
            <Title
                markup={titleMarkup}
                level={titleLevel}
                typo={TypographyAlign.TEXT_CENTERED}
                accessibilityLabel={titleAlt}
                className='whitespace-pre-line'
            >
                {title}
            </Title>
            {children}
        </FlexBox>
    );
}

function FaqContainerWithSection({ children, ...props }: Readonly<FaqContainerProps>) {
    return (
        <SectionContainer>
            <FaqContainer {...props}>{children}</FaqContainer>
        </SectionContainer>
    );
}

export function Faq({
    url,
    title,
    titleAlt,
    titleMarkup,
    titleLevel,
    withSection = false,
    className = '',
}: Readonly<FaqProps>) {
    if (!url) {
        return null;
    }

    return (
        <SSIBlock
            url={partials.FAQ(url)}
            container={withSection ? FaqContainerWithSection : FaqContainer}
            containerProps={{ title, titleAlt, titleMarkup, titleLevel, className }}
        />
    );
}
